/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
    Form as FormikForm,
    DatePickerField,
    DateTimePickerField,
    AutocompleteField,
    InputField,
    TextareaField,
    Masks,
    RadioField,
} from "components/common_components/Formik";
import * as Yup from "yup";
import Button from "components/common_components/Button";
import { DataSearchDialog } from "components/common_components/Dialogs";
import Icon from "components/common_components/Icon";
import FileCaseEntity from "entities/ImmigrationPersonalInfo";
import * as EntityUtils from "entities/utils";
import ModulesContext from "../Module/ModulesContext";
import ParentInfoControl from "./ParentInfoControl";
import { getDateMillis } from "utils/dateUtils";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import ClientInfo from "./ClientInfo";
import { Dialog, DialogActions, DialogContent } from "@material-ui/core";
import CaseStatusCard from "./CustomComponents/Immigration/CaseStatusCard";

function Form({ fileCase, resources, mode, onEdit, prefix, consecutive, onSubmit, ...rest }) {
    const history = useHistory();
    const readOnly = mode === "detail";
    const { types, status, fileLocations } = resources;
    const [initialValues, setInitialValues] = useState(undefined);
    const [caseStatusOpen, setCaseStatusOpen] = useState(false);
    const [isCaseStatusLoading, setIsCaseStatusLoading] = useState(false);
    const [caseStatusData, setCaseStatusData] = useState(null);
    const [caseStatusError, setCaseStatusError] = useState(null);

    const { Customers, getClientPhotoAsync, permissions, PersonalInfo } = useContext(ModulesContext);

    function init() {
        if (mode === "add") {
            setInitialValues({
                ...EntityUtils.mapEntity(FileCaseEntity, fileCase),
                prefix,
                consecutive: Number(consecutive),
                clientHasDualCitizenship: "",
                clientHasEnteredMultipleTimesToUsa: "",
            });
        } else {
            const { clientHasDualCitizenship, clientHasEnteredMultipleTimesToUsa, ...values } = {
                ...EntityUtils.mapEntity(FileCaseEntity, fileCase),
            };
            const newClientHasDualCitizenship = clientHasDualCitizenship === true ? "yes" : clientHasDualCitizenship === false ? "no" : "";
            const newClientHasEnteredMultipleTimesToUsa =
                clientHasEnteredMultipleTimesToUsa === true ? "yes" : clientHasEnteredMultipleTimesToUsa === false ? "no" : "";
            setInitialValues({
                ...values,
                clientHasDualCitizenship: newClientHasDualCitizenship,
                clientHasEnteredMultipleTimesToUsa: newClientHasEnteredMultipleTimesToUsa,
            });
        }
    }

    useEffect(() => {
        init();
    }, [fileCase]);

    const validationSchema = Yup.object({
        prefix: Yup.string().required("Required field").max(5),
        consecutive: Yup.number().min(1).required("Required field"),
        notes: Yup.string().notRequired().nullable().max(500),
        alienRegistrationNo: Yup.string().notRequired().nullable().max(50),
        howEntered: Yup.string().notRequired().nullable().max(100),
        petitioner: Yup.string().notRequired().nullable().max(50),
        petitionerANo: Yup.string().notRequired().nullable().max(50),
        petitionerNatCert: Yup.string().notRequired().nullable().max(50),
        petitionerSSN: Yup.string().notRequired().nullable().max(30),
        placeOfEntry: Yup.string().notRequired().nullable().max(100),
        srcNo: Yup.string().notRequired().nullable().max(50),
        courtLocation: Yup.string().notRequired().nullable().max(50),
        parentId: Yup.number().notRequired().nullable(),
        clientHasDualCitizenship: Yup.string().required("Required field"),
        clientHasEnteredMultipleTimesToUsa: Yup.string().required("Required field"),
        receiptNumber: Yup.string()
            .notRequired()
            .nullable()
            .length(13, "Receipt number must be 13 characters long")
            .matches(/^([a-zA-Z]{3}[0-9]{10}|[a-zA-Z]{3}\*[0-9]{9})$/, "Invalid format. Use: ABC1234567890 or ABC*123456789"),
    });

    function filterClientsBuilder(filterValue) {
        return filterValue
            ? {
                  firstName: filterValue,
                  lastName: filterValue,
                  middleName: filterValue,
                  homePhone: filterValue,
                  ssn: filterValue,
              }
            : {};
    }

    const [searchClientDialogProps, setSearchClientDialogProps] = useState({
        open: false,
        title: "Select a client",
        onClose: () => setSearchClientDialogProps((prev) => ({ ...prev, open: false })),
        onPrimaryClick: undefined,
        onSecondaryClick: undefined,
        tableProps: {
            defaultOrder: { order: "asc", orderBy: "firstName" },
            keyColumnName: "id",
            selectable: true,
            allowSelectAll: false,
            allowMultiSelect: false,
            dense: false,
            deletable: false,
            onRequestData: Customers.getListAsync,
            filterBuilder: filterClientsBuilder,
        },
    });

    function setCustomer(selectedItems, { values: currentValues, setValues, setFieldValue }) {
        const client = selectedItems[0];
        if (client) {
            getClientPhotoAsync(client.id);
            setValues({
                ...currentValues,
                notes: client.notes,
                client: { ...client, country: client.country || null, birthDate: client.birthDate || null },
            });
        }
    }

    function goToCustomer(customerId) {
        history.push(`/customers/${customerId}`);
    }

    function searchCustomer(form) {
        const headCells = [
            { index: 1, id: "firstName", disablePadding: false, label: "First Name" },
            { index: 2, id: "lastName", disablePadding: false, label: "Last Name" },
            { index: 3, id: "homePhone", disablePadding: true, label: "Home Phone" },
            { index: 4, id: "birthDateString", disablePadding: true, label: "Date of Birth" },
            { index: 5, id: "ssn", disablePadding: true, label: "SSN" },
        ];
        setSearchClientDialogProps((prev) => ({
            ...prev,
            open: true,
            onPrimaryClick: (selectedItems) => setCustomer(selectedItems, form),
            tableProps: {
                headCells,
                ...prev.tableProps,
            },
        }));
    }

    function onSubmitHandler(values, form) {
        const {
            startDate,
            dateOfEntry,
            courtDate,
            supplementDeadline,
            otherDeadline,
            individualCourtDate,
            clientHasDualCitizenship,
            clientHasEnteredMultipleTimesToUsa,
            ...payload
        } = values;

        onSubmit(
            {
                ...payload,
                startDate: getDateMillis(startDate),
                dateOfEntry: getDateMillis(dateOfEntry),
                courtDate: getDateMillis(courtDate),
                supplementDeadline: getDateMillis(supplementDeadline),
                otherDeadline: getDateMillis(otherDeadline),
                individualCourtDate: getDateMillis(individualCourtDate),
                clientHasDualCitizenship: clientHasDualCitizenship === "yes",
                clientHasEnteredMultipleTimesToUsa: clientHasEnteredMultipleTimesToUsa === "yes",
            },
            form
        );
    }

    async function openCaseStatus(receiptNumber) {
        setIsCaseStatusLoading(true);
        setCaseStatusOpen(true);
        try {
            const resp = await PersonalInfo.getUscisCaseStatusAsync?.(fileCase.id, receiptNumber);
            setCaseStatusData(resp.case_status);
            console.log(resp.message);
        } catch (error) {
            debugger;
            setCaseStatusError(error);
            console.error("Error fetching case status:", error);
        } finally {
            setIsCaseStatusLoading(false);
        }
    }

    return (
        <>
            {initialValues && (
                <FormikForm
                    validateOnChange={false}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    showButtons={!readOnly}
                    onSubmit={onSubmitHandler}
                    {...rest}
                >
                    {(form) => (
                        <>
                            <Grid container spacing={3}>
                                <Grid item md={12} lg={7}>
                                    <Grid container direction="row" spacing={1}>
                                        <Grid item xs={12} md={2}>
                                            <InputField disabled fieldName="prefix" uppercase margin="dense" />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <InputField
                                                disabled={mode !== "add"}
                                                fieldName="consecutive"
                                                label="File No *"
                                                uppercase
                                                margin="dense"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                                                <Grid item>
                                                    {mode === "add" && (
                                                        <Button link onClick={() => searchCustomer(form)}>
                                                            <Icon name="search" size="xs" color="primary" />
                                                            <span>Search client</span>
                                                        </Button>
                                                    )}
                                                    {form.values.client.id && (
                                                        <Button link onClick={() => goToCustomer(form.values.client.id)}>
                                                            Go to client
                                                        </Button>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            {fileLocations && (
                                                <AutocompleteField
                                                    disabled={readOnly}
                                                    fieldName="fileLocation"
                                                    label="File Location"
                                                    options={fileLocations}
                                                    valueKey="code"
                                                    labelKey="name"
                                                    margin="dense"
                                                    getOptionLabel={({ name }) => name}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider light />
                                        </Grid>
                                    </Grid>
                                    <ParentInfoControl mode={mode} />
                                    <Grid item xs={12}>
                                        <Divider light />
                                    </Grid>
                                    <ClientInfo />
                                    <Grid item xs={12}>
                                        <Divider light />
                                    </Grid>
                                    <Grid container direction="row" spacing={1}>
                                        <Grid item xs={12} md={6}>
                                            <InputField disabled={readOnly} fieldName="courtLocation" label="Court Location" />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DateTimePickerField disabled={readOnly} fieldName="courtDate" label="Master Court Date" />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={4}>
                                            <DateTimePickerField
                                                disabled={readOnly}
                                                fieldName="individualCourtDate"
                                                label="Individual Court Date"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={4}>
                                            <DateTimePickerField
                                                disabled={readOnly}
                                                fieldName="supplementDeadline"
                                                label="Supplement deadline"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={4}>
                                            <DateTimePickerField disabled={readOnly} fieldName="otherDeadline" label="Other deadline" />
                                        </Grid>
                                        <Grid item xs={12} />
                                        <Grid item xs={12}>
                                            <Divider light />
                                        </Grid>
                                    </Grid>
                                    <Grid container direction="row" spacing={1}>
                                        <Grid item xs={12} md={6}>
                                            <InputField disabled={readOnly} fieldName="petitioner" label="Petitioner" />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputField disabled={readOnly} fieldName="petitionerANo" label="Petitioner A#" />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputField disabled={readOnly} fieldName="petitionerNatCert" label="Petitioner Nat Cert" />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputField
                                                disabled={readOnly}
                                                fieldName="petitionerSSN"
                                                label="Petitioner S.S. No."
                                                mask={Masks.ssn}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Grid container direction="row" spacing={1} alignItems="center">
                                                <Grid item xs={10}>
                                                    <InputField
                                                        disabled={readOnly}
                                                        fieldName="receiptNumber"
                                                        label="USCIS - Receipt Number"
                                                        maxLength={13}
                                                    />
                                                </Grid>
                                                <Grid item xs={1}>
                                                    {form.values.receiptNumber && (
                                                        <Button
                                                            secondary
                                                            small
                                                            outline
                                                            onClick={() => openCaseStatus(form.values.receiptNumber)}
                                                        >
                                                            <Icon name="open-new-tab" size="sm" color="secondary" />
                                                        </Button>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item md={12} lg={5}>
                                    <Grid container direction="row" spacing={1}>
                                        <Grid item xs={12} md={6}>
                                            <DatePickerField disabled={readOnly} fieldName="startDate" label="Start Date" />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DatePickerField disabled={readOnly} fieldName="dateOfEntry" label="Date of Entry" />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputField disabled={readOnly} fieldName="placeOfEntry" label="Place of Entry" />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputField disabled={readOnly} fieldName="howEntered" label="How Entered" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            {types && (
                                                <AutocompleteField
                                                    disabled={readOnly}
                                                    fieldName="type"
                                                    label="Case Type"
                                                    options={types}
                                                    valueKey="code"
                                                    labelKey="type"
                                                    getOptionLabel={({ type }) => type}
                                                    getOptionSelected={(opt, val) => opt.id === val.id}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputField disabled={readOnly} fieldName="srcNo" label="SRC No." />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputField disabled fieldName="client.ssn" label="S.S No." mask={Masks.ssn} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputField
                                                disabled={readOnly}
                                                fieldName="alienRegistrationNo"
                                                label="Alien Registration No."
                                                mask={Masks.alienRegistrationNo}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {status && (
                                                <AutocompleteField
                                                    disabled={readOnly}
                                                    fieldName="status"
                                                    label="Status"
                                                    options={status}
                                                    valueKey="code"
                                                    labelKey="name"
                                                    getOptionLabel={({ name }) => name}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextareaField disabled={readOnly} fieldName="notes" label="Notes" fullWidth maxLength={500} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <RadioField
                                                options={[
                                                    { value: "yes", label: "Yes" },
                                                    { value: "no", label: "No" },
                                                ]}
                                                fieldName="clientHasDualCitizenship"
                                                label="Has dual citizenship"
                                                readOnly={readOnly}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <RadioField
                                                options={[
                                                    { value: "yes", label: "Yes" },
                                                    { value: "no", label: "No" },
                                                ]}
                                                fieldName="clientHasEnteredMultipleTimesToUsa"
                                                label="Has entered multiple times to USA"
                                                readOnly={readOnly}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {readOnly && (
                                <div className="button-group">
                                    <Button outline onClick={rest.onCancel}>
                                        Back
                                    </Button>
                                    {permissions.personalInfo.actions.edit && (
                                        <Button primary onClick={onEdit}>
                                            Edit
                                        </Button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </FormikForm>
            )}
            <DataSearchDialog {...searchClientDialogProps} />
            <Dialog open={caseStatusOpen} maxWidth="sm" keepMounted fullWidth>
                <DialogContent>
                    <CaseStatusCard data={caseStatusData} loading={isCaseStatusLoading} error={caseStatusError} />
                </DialogContent>
                <DialogActions>
                    <Button secondary small onClick={() => setCaseStatusOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

Form.propTypes = {
    fileCase: PropTypes.shape(EntityUtils.getEntityTypes(FileCaseEntity)),
    resources: PropTypes.shape({
        types: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                type: PropTypes.string,
            })
        ),
        status: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            })
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
    prefix: PropTypes.string.isRequired,
    consecutive: PropTypes.number.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

Form.defaultProps = {
    fileCase: EntityUtils.getEntityDefaults(FileCaseEntity),
    resources: {
        types: [],
        status: [],
    },
    onEdit: () => {},
};

export default Form;
